import { useCallback, useEffect, useRef, useState } from 'react';
import { getSlotTiers } from '@/lib/api/public';
import { PUBLISHED_SLOT_TIERS } from '@/lib/publishedSlotTiers';
import { PAID_SLOT_TIERS, normaliseSlotTiers } from '@/lib/slotPricing';

/**
 * The slot tier catalogue for the pricing calculator (GET /api/slots/tiers via getSlotTiers).
 * → { tiers, status, retry }
 * - 'loading': first load or a retry in progress (tiers: the last good list, [] at first)
 * - 'ready':   live tiers (normalised, SLOT_TIER_ORDER, free listing included)
 * - 'fallback': the catalogue is not published (404, an empty list, or no paid tier) →
 *              the published list prices (lib/publishedSlotTiers), shown without comment
 * - 'error':   the API failed otherwise (network, 5xx) → tiers: [] and the caller offers
 *              `retry()`; prices are never invented
 */
export default function usePricingTiers() {
  const [state, setState] = useState({ status: 'loading', tiers: [] });
  const request = useRef(0);
  const alive = useRef(true);

  const load = useCallback(() => {
    request.current += 1;
    const id = request.current;
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.resolve()
      .then(() => getSlotTiers())
      .then((res) => {
        if (!alive.current || id !== request.current) return;
        const tiers = normaliseSlotTiers(res && res.data);
        if (tiers.some((t) => PAID_SLOT_TIERS.includes(t.tier_key))) setState({ status: 'ready', tiers });
        else setState({ status: 'fallback', tiers: PUBLISHED_SLOT_TIERS });
      })
      .catch((err) => {
        if (!alive.current || id !== request.current) return;
        if (err && err.response && err.response.status === 404) setState({ status: 'fallback', tiers: PUBLISHED_SLOT_TIERS });
        else setState({ status: 'error', tiers: [] });
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  return { tiers: state.tiers, status: state.status, retry: load };
}
